import { baseLayersProperties } from '@constants/canvas/general';
import { ImpactType } from '@store/services/projects/types';
import { themePalette } from '@theme/muiTheme';

export const CanvasTheme = {
  line: {
    default: { stroke: themePalette.grey[700], width: 2 },
    faded: { stroke: themePalette.grey[600], width: 2 },
    /** Same colour as Faded by design; the spec separates them by width. */
    disabled: { stroke: themePalette.grey[600], width: 1 },
    /** Hover and Selected share one look; only Selected adds the dash. */
    focused: { stroke: themePalette.grey[1000], width: 3 },
    selectedDash: '7 4',
  },
  entity: {
    default: {
      border: themePalette.grey[550],
      surface: themePalette.white,
      shadow:
        '0 1px 2px 0 rgba(16, 24, 40, 0.06), 0 1px 3px 0 rgba(16, 24, 40, 0.1)',
    },
    /** Hover, Selected, multi-select and insight-select all read the same. */
    active: { border: themePalette.green[900] },
    faded: { border: themePalette.grey[500] },
    disabled: {
      border: themePalette.grey[500],
      surface: themePalette.grey[400],
      blur: '1px',
    },
  },
  connectorDot: {
    default: themePalette.grey[700],
    faded: themePalette.grey[600],
    active: { fill: themePalette.white, ring: themePalette.green[900] },
  },
} as const;

/**
 * Layer order, bottom to top.
 */
export const CanvasZIndex = {
  /** Equal to the layer separators by construction, so the two cannot drift. */
  Separators: baseLayersProperties.zIndex,
  Disabled: 0,
  Line: 1,
  Entity: 2,
  FocusedLine: 3,
  FocusedEndpoint: 4,
} as const;

/** Invisible hit path width, in flow units (~10 screen px at 50% zoom). */
export const EDGE_INTERACTION_WIDTH = 20;

/** Key-controls impact badge and entity border, by impact level. */
export const KeyControlsImpactColor: Record<ImpactType, string> = {
  [ImpactType.High]: themePalette.system.error,
  [ImpactType.Medium]: themePalette.system.alert,
  [ImpactType.Low]: themePalette.system.success,
};

/**
 * Risk-score badge border, keyed by the chip variant `useScoreColor` returns.
 * The variant names come from project settings ranges, not from here.
 */
export const RiskScoreColor: Record<string, string> = {
  grey: themePalette.system.success,
  green: themePalette.system.success,
  yellow: themePalette.system.alert,
  red: themePalette.system.error,
};

/**
 * Maturity thresholds. The canvas does not import these directly: `Maturity`
 * computes the colour and injects it as `data.canvas.maturityBackground`, which
 * CustomNode renders. They live here so every canvas-facing threshold sits in
 * one file; the Insights Maturity views re-export them.
 */
export const getMaturityColor = (maturity: number) => {
  switch (true) {
    case maturity >= 4 && maturity <= 5:
      return themePalette.system.success;
    case maturity >= 2.5 && maturity < 4:
      return themePalette.system.alert;
    case maturity >= 0 && maturity < 2.5:
      return themePalette.system.error;
    default:
      return themePalette.grey[900];
  }
};

export const getMaturityLabelBackground = (maturity: number) => {
  switch (true) {
    case maturity >= 4 && maturity <= 5:
      return themePalette.green[500];
    case maturity >= 2.5 && maturity < 4:
      return themePalette.yellow[600];
    case maturity >= 0 && maturity < 2.5:
      return themePalette.red[600];
    default:
      return themePalette.grey[500];
  }
};
